import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  API_get_actual_used_schedule,
  API_get_effective_wages,
} from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  formatShiftTime,
  displayName,
  formatCurrency,
  formatHours,
  isNoShowActualShift,
  shiftDurationHours,
  clockInVarianceMinutes,
  clockInStatus,
  varianceDollars,
  lookupEffectiveWageOnDate,
} from "./_shared/laborMath";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

// Constant copied locally — same pattern as Punch Information / Daily Preview.
// Once a third report needs it we'll lift to a shared module.
function fetchStoreScopedRange(api, storeId, startStr, endStr) {
  const url = `${api}${storeId}&date_start=${startStr}&date_end=${endStr}`;
  return axios.get(url, getDefaultAxiosConfig());
}

const STATUS_STYLE = {
  early: "bg-amber-50 text-amber-700",
  late: "bg-rose-50 text-rose-700",
  "on-time": "bg-emerald-50 text-emerald-700",
  missing: "bg-slate-100 text-slate-500",
};

const STATUS_LABEL = {
  early: "Early",
  late: "Late",
  "on-time": "On time",
  missing: "—",
};

function StatusPill({ status }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded ${
        STATUS_STYLE[status] || STATUS_STYLE.missing
      }`}
    >
      {STATUS_LABEL[status] || "—"}
    </span>
  );
}

function formatVarianceMinutes(v) {
  if (v == null) return "—";
  if (v === 0) return "0";
  return v > 0 ? `+${v}` : String(v);
}

export default function LaborSchedulingRosterReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
}) {
  // Opens on the store's last completed business day: the report is built
  // from actuals, which aren't complete until the business day ends.
  const [selectedDate, setSelectedDate] = useState(() =>
    lastCompletedBusinessDay(storeNames, homeStore)
  );

  const incrementDay = useCallback((current, days) => {
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
  }, []);

  const dateStr = useMemo(
    () => dayjs(selectedDate).format("YYYY-MM-DD"),
    [selectedDate]
  );
  // Stores with start_qhnum > 0 attribute overnight punches to the
  // previous business day; widen the actual fetch by ±1 calendar day so
  // those rows are returned, then trim by business_date when building
  // jobRows below.
  const actualFetchStart = useMemo(
    () => dayjs(selectedDate).subtract(1, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );
  const actualFetchEnd = useMemo(
    () => dayjs(selectedDate).add(1, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );

  const [rows, setRows] = useState([]);
  // Range-aware wage data from get_effective_wages — single day in this
  // report's case, but we go through lookupEffectiveWageOnDate so the lookup
  // stays correct if the day happens to fall on a pay-period boundary.
  const [wageData, setWageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        // Fetch the roster and the payroll-grade wage snapshot in parallel.
        // The wage snapshot powers the variance-dollar column via
        // lookupEffectiveWage; if it fails we just lose $ figures, not the
        // whole report.
        const [rosterRes, wagesRes] = await Promise.all([
          fetchStoreScopedRange(
            API_get_actual_used_schedule,
            homeStore,
            actualFetchStart,
            actualFetchEnd
          ),
          getRightworkAPI(API_get_effective_wages, {
            store_id: homeStore,
            date_start: dateStr,
            date_end: dateStr,
          }).catch(() => null),
        ]);
        if (cancelled) return;
        setRows(Array.isArray(rosterRes?.data) ? rosterRes.data : []);
        setWageData(wagesRes?.data ?? null);
      } catch (e) {
        if (cancelled) return;
        setError("Couldn't load roster. Try again or pick a different day.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, dateStr, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // For each row from the API, derive scheduled/actual/variance details.
  // We keep all groupings ("by job") in one pass so the row math doesn't
  // run twice.
  const groupedByJob = useMemo(() => {
    const byJob = new Map();
    const totals = { earlyMin: 0, lateMin: 0, earlyDollars: 0, lateDollars: 0 };

    for (const r of rows) {
      // Trim the ±1 day over-fetch back to the selected business date.
      // For stores with start_qhnum=0, business_date equals date_start for
      // every row, so this is a no-op.
      const rBusinessDate = r.business_date || r.date_start;
      if (rBusinessDate !== dateStr) continue;
      const published = r.published;
      const stm =
        r.store_team_member ||
        published?.store_team_member ||
        null;
      const role = published?.role_id || null;
      const jobName = role?.role_name || "Unassigned";

      // Scheduled side (from published). `hours` is already net of breaks
      // per AbstractBaseSchedule (shift_length − break_length − break_length_2).
      const schedStart = published?.shift_start ?? null;
      const schedEnd = published?.shift_end ?? null;
      const schedHours = published?.hours ?? null;
      const schedBreak =
        published == null
          ? null
          : (Number(published.break_length) || 0) +
            (Number(published.break_length_2) || 0);

      // Actual side (top-level on r). total_break is the unpaid-break total
      // already subtracted from worked hours.
      //
      // No-show placeholder rows have shift_start = shift_end = 0 — we keep
      // the row visible because the scheduled-vs-actual contrast is
      // meaningful for a roster, but null out the actual times so the
      // table renders "—", the status pill reads "missing" instead of an
      // extreme "Early" pill, and the variance totals at the bottom skip
      // the row via the null-guarded clockInVariance helpers.
      const isNoShow = isNoShowActualShift(r);
      const actStart = isNoShow ? null : r.shift_start ?? null;
      const actEnd = isNoShow ? null : r.shift_end ?? null;
      const actBreak = isNoShow ? null : r.total_break ?? null;
      const actHours = isNoShow
        ? null
        : shiftDurationHours(actStart, actEnd, r.total_break);

      // Variance
      const varianceMin = clockInVarianceMinutes(schedStart, actStart);
      const status = clockInStatus(varianceMin);
      // Wage lookup prefers the role the employee actually clocked into
      // (covers role-swap cases). Falls back to the scheduled role, then
      // general. Same precedence the Daily Punch Audit uses.
      const scheduledRoleId =
        typeof role === "object" && role !== null
          ? role?.id ?? null
          : role ?? null;
      const wageRoleId = r.actual_role_id ?? scheduledRoleId;
      const wage = lookupEffectiveWageOnDate(
        wageData,
        rBusinessDate || dateStr,
        stm?.id,
        wageRoleId
      );
      const varianceDol = varianceDollars(varianceMin, wage);

      // Footer rollups (only signed-in shifts contribute)
      if (varianceMin != null) {
        if (varianceMin < 0) {
          totals.earlyMin += -varianceMin;
          if (varianceDol != null) totals.earlyDollars += Math.abs(varianceDol);
        } else if (varianceMin > 0) {
          totals.lateMin += varianceMin;
          if (varianceDol != null) totals.lateDollars += Math.abs(varianceDol);
        }
      }

      const row = {
        key: `${rBusinessDate}-${stm?.id ?? "?"}-${actStart ?? "x"}-${
          schedStart ?? "x"
        }`,
        employee: displayName(stm),
        note: published?.note || "",
        // "DOB" (Day Of Business) — must be the business date for stores
        // with start_qhnum > 0; falling back to date_start would print
        // the calendar clock-in date for overnight shifts.
        dob: rBusinessDate || null,
        schedStart,
        schedEnd,
        schedBreak,
        schedHours,
        actStart,
        actEnd,
        actBreak,
        actHours,
        varianceMin,
        varianceDol,
        status,
      };

      if (!byJob.has(jobName)) byJob.set(jobName, []);
      byJob.get(jobName).push(row);
    }

    const groups = Array.from(byJob.entries())
      .map(([job, items]) => ({
        job,
        rows: items.slice().sort((a, b) => a.employee.localeCompare(b.employee)),
      }))
      .sort((a, b) => a.job.localeCompare(b.job));

    return { groups, totals };
  }, [rows, wageData, dateStr]);

  const filters = (
    <div className="flex items-center">
      <DateSelector date={selectedDate} increment_day={incrementDay} />
    </div>
  );

  const fullDateLabel = dayjs(selectedDate).format("dddd, MMMM D, YYYY");

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : groupedByJob.groups.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No shifts on this day.
        </div>
      ) : (
        <>
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-slate-700">
              {fullDateLabel}
            </h4>
            <p className="text-xs text-slate-500">
              Scheduled vs. actual clock times per shift, grouped by job.
              Variance dollars use each employee's effective hourly rate
              (no OT/DT multipliers); for payroll-grade per-shift pay, use
              the Daily Punch Audit.
            </p>
          </div>

          <div className="space-y-4">
            {groupedByJob.groups.map(({ job, rows: jobRows }) => (
              <div
                key={job}
                className="border border-slate-200 rounded-lg overflow-hidden"
              >
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-baseline justify-between">
                  <h5 className="text-sm font-semibold text-slate-800">{job}</h5>
                  <span className="text-[11px] text-slate-500">
                    {jobRows.length} shift{jobRows.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs table-fixed">
                    <colgroup>
                      <col style={{ width: "200px" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "80px" }} />
                      <col style={{ width: "80px" }} />
                      <col style={{ width: "70px" }} />
                      <col style={{ width: "70px" }} />
                      <col style={{ width: "80px" }} />
                      <col style={{ width: "80px" }} />
                      <col style={{ width: "70px" }} />
                      <col style={{ width: "70px" }} />
                      <col style={{ width: "90px" }} />
                      <col style={{ width: "70px" }} />
                      <col style={{ width: "80px" }} />
                    </colgroup>
                    <thead className="border-b border-slate-100 text-slate-500">
                      <tr>
                        <th
                          rowSpan={2}
                          className="text-left font-medium px-3 py-1.5 align-bottom"
                        >
                          Employee
                        </th>
                        <th
                          rowSpan={2}
                          className="text-left font-medium px-3 py-1.5 align-bottom"
                        >
                          DOB
                        </th>
                        <th
                          colSpan={4}
                          className="text-center font-medium px-3 py-1 border-l border-slate-100"
                        >
                          Scheduled
                        </th>
                        <th
                          colSpan={4}
                          className="text-center font-medium px-3 py-1 border-l border-slate-100"
                        >
                          Actual
                        </th>
                        <th
                          colSpan={3}
                          className="text-center font-medium px-3 py-1 border-l border-slate-100"
                        >
                          Clock-in Variance
                        </th>
                      </tr>
                      <tr className="text-[10px]">
                        <th className="text-left font-medium px-3 py-1 border-l border-slate-100">
                          In
                        </th>
                        <th className="text-left font-medium px-3 py-1">Out</th>
                        <th className="text-right font-medium px-3 py-1">
                          Break
                        </th>
                        <th className="text-right font-medium px-3 py-1">
                          Hrs
                        </th>
                        <th className="text-left font-medium px-3 py-1 border-l border-slate-100">
                          In
                        </th>
                        <th className="text-left font-medium px-3 py-1">Out</th>
                        <th className="text-right font-medium px-3 py-1">
                          Break
                        </th>
                        <th className="text-right font-medium px-3 py-1">
                          Hrs
                        </th>
                        <th className="text-left font-medium px-3 py-1 border-l border-slate-100">
                          Status
                        </th>
                        <th className="text-right font-medium px-3 py-1">
                          Min
                        </th>
                        <th className="text-right font-medium px-3 py-1">$</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobRows.map((r) => (
                        <tr
                          key={r.key}
                          className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2 font-medium text-slate-800 truncate">
                            {r.employee}
                            {r.note && (
                              <div className="text-[10px] text-slate-500 font-normal truncate">
                                {r.note}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {r.dob ? dayjs(r.dob).format("M/D/YYYY") : "—"}
                          </td>
                          <td className="px-3 py-2 text-slate-700 border-l border-slate-100">
                            {formatShiftTime(r.schedStart)}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {formatShiftTime(r.schedEnd)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.schedBreak)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.schedHours)}
                          </td>
                          <td className="px-3 py-2 text-slate-700 border-l border-slate-100">
                            {formatShiftTime(r.actStart)}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {formatShiftTime(r.actEnd)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.actBreak)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatHours(r.actHours)}
                          </td>
                          <td className="px-3 py-2 border-l border-slate-100">
                            <StatusPill status={r.status} />
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {formatVarianceMinutes(r.varianceMin)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {r.varianceDol == null
                              ? "—"
                              : formatCurrency(r.varianceDol)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Variance totals — matches Compeat's footer block */}
          <div className="mt-6 border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
              <h5 className="text-sm font-semibold text-slate-800">
                Variance Totals
              </h5>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Based on clock-in time vs. scheduled start. For total worked
                time, compare Scheduled Hrs to Actual Hrs in each row.
              </p>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-100">
                  <th className="text-left font-medium px-3 py-1.5"></th>
                  <th className="text-right font-medium px-3 py-1.5">
                    Minutes
                  </th>
                  <th className="text-right font-medium px-3 py-1.5">$</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-50">
                  <td className="px-3 py-2 text-slate-700">Only Earlies</td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {groupedByJob.totals.earlyMin}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatCurrency(groupedByJob.totals.earlyDollars)}
                  </td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="px-3 py-2 text-slate-700">Only Tardies</td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {groupedByJob.totals.lateMin}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatCurrency(groupedByJob.totals.lateDollars)}
                  </td>
                </tr>
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-3 py-2 text-slate-800">Net</td>
                  <td className="px-3 py-2 text-right text-slate-900">
                    {groupedByJob.totals.lateMin - groupedByJob.totals.earlyMin}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-900">
                    {formatCurrency(
                      groupedByJob.totals.lateDollars -
                        groupedByJob.totals.earlyDollars
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </PrebuiltReportShell>
  );
}
